import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import {
  journalApprovalWorkflowsApi,
  journalApprovalInstancesApi,
} from '@/services/journalApprovalService';

interface JournalApprovalState {
  workflows: any[];
  pendingInstances: any[];
  currentWorkflow: any;
  loading: boolean;
  error: any;
  totalRows: number;
  lastParams?: Record<string, any>;
}

const initialState: JournalApprovalState = {
  workflows: [],
  pendingInstances: [],
  currentWorkflow: null,
  loading: false,
  error: null,
  totalRows: 0,
  lastParams: undefined,
};

/**
 * `error.response.data` is the raw backend payload — SQL fragments, stack
 * hints, internal ids — and `state.error` is rendered straight into the UI, so
 * rejecting with it leaks server internals. Return a plain, user-facing string
 * and keep the raw error in the console for debugging.
 */
function toSafeError(error: any): string {
  console.error('[journalApproval] request failed:', error);
  const payload = error?.response?.data;
  const message =
    (typeof payload === 'string' ? payload : payload?.message) ??
    (typeof error?.message === 'string' ? error.message : '');
  return message && message.length <= 300 ? message : 'An unexpected error occurred.';
}

export const fetchWorkflows = createAsyncThunk(
  'journalApproval/fetchWorkflows',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await journalApprovalWorkflowsApi.getAll(params || {});
      const data = response?.data?.data;
      // The endpoint answers either `{ workflows: [...] }`, a paginated
      // `{ data: [...] }`, or a bare array — only accept the array candidates
      // so `state.workflows` keeps its array invariant.
      const candidate = [data?.workflows, data?.data, data].find(Array.isArray);
      return {
        workflows: candidate ?? [],
        total: data?.pagination?.total || 0,
      };
    } catch (error: any) {
      return rejectWithValue(toSafeError(error));
    }
  }
);

export const fetchWorkflow = createAsyncThunk(
  'journalApproval/fetchWorkflow',
  async (id: number | string, { rejectWithValue }) => {
    try {
      const response: any = await journalApprovalWorkflowsApi.getById(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(toSafeError(error));
    }
  }
);

export const createWorkflow = createAsyncThunk(
  'journalApproval/createWorkflow',
  async (data: any, { rejectWithValue, dispatch, getState }) => {
    try {
      const response: any = await journalApprovalWorkflowsApi.create(data);
      // Re-fetch with the last-used params so pagination/filters survive.
      dispatch(fetchWorkflows((getState() as any).journalApproval.lastParams));
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(toSafeError(error));
    }
  }
);

export const updateWorkflow = createAsyncThunk(
  'journalApproval/updateWorkflow',
  async ({ id, data }: { id: number | string, data: any }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response: any = await journalApprovalWorkflowsApi.update(id, data);
      dispatch(fetchWorkflows((getState() as any).journalApproval.lastParams));
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(toSafeError(error));
    }
  }
);

export const deleteWorkflow = createAsyncThunk(
  'journalApproval/deleteWorkflow',
  async (id: number | string, { rejectWithValue, dispatch, getState }) => {
    try {
      await journalApprovalWorkflowsApi.delete(id);
      dispatch(fetchWorkflows((getState() as any).journalApproval.lastParams));
      return id;
    } catch (error: any) {
      return rejectWithValue(toSafeError(error));
    }
  }
);

export const fetchPendingInstances = createAsyncThunk(
  'journalApproval/fetchPendingInstances',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await journalApprovalInstancesApi.getPending(params || {});
      const data = response.data.data;
      return data.instances || data.data || data || [];
    } catch (error: any) {
      return rejectWithValue(toSafeError(error));
    }
  }
);

export const approveInstance = createAsyncThunk(
  'journalApproval/approveInstance',
  async ({ id, data }: { id: number | string, data?: Record<string, any> }, { rejectWithValue, dispatch }) => {
    try {
      const response: any = await journalApprovalInstancesApi.approve(id, data || {});
      dispatch(fetchPendingInstances(undefined));
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(toSafeError(error));
    }
  }
);

export const rejectInstance = createAsyncThunk(
  'journalApproval/rejectInstance',
  async ({ id, data }: { id: number | string, data?: Record<string, any> }, { rejectWithValue, dispatch }) => {
    try {
      const response: any = await journalApprovalInstancesApi.reject(id, data || {});
      dispatch(fetchPendingInstances(undefined));
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(toSafeError(error));
    }
  }
);

const journalApprovalSlice = createSlice({
  name: 'journalApproval',
  initialState,
  reducers: {
    clearState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Every thunk signals pending/rejected: without them a failed
    // create/update/delete/approve/reject left no error and a permanently
    // false loading flag, so the UI silently did nothing.
    const handlePending = (state: JournalApprovalState) => {
      state.loading = true;
      state.error = null;
    };
    const handleRejected = (state: JournalApprovalState, action: PayloadAction<unknown>) => {
      state.loading = false;
      state.error = action.payload;
    };

    builder
      .addCase(fetchWorkflows.pending, handlePending)
      .addCase(fetchWorkflows.fulfilled, (state, action) => {
        state.loading = false;
        state.workflows = action.payload.workflows;
        state.totalRows = action.payload.total || action.payload.workflows.length;
        // Remember the query context so post-mutation refetches keep it.
        state.lastParams = action.meta.arg;
      })
      .addCase(fetchWorkflows.rejected, handleRejected)

      .addCase(fetchWorkflow.pending, handlePending)
      .addCase(fetchWorkflow.fulfilled, (state, action) => {
        state.loading = false;
        state.currentWorkflow = action.payload;
      })
      .addCase(fetchWorkflow.rejected, handleRejected)

      .addCase(fetchPendingInstances.pending, handlePending)
      .addCase(fetchPendingInstances.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingInstances = action.payload;
      })
      .addCase(fetchPendingInstances.rejected, handleRejected)

      .addCase(createWorkflow.pending, handlePending)
      .addCase(createWorkflow.rejected, handleRejected)
      .addCase(updateWorkflow.pending, handlePending)
      .addCase(updateWorkflow.rejected, handleRejected)
      .addCase(deleteWorkflow.pending, handlePending)
      .addCase(deleteWorkflow.rejected, handleRejected)
      .addCase(approveInstance.pending, handlePending)
      .addCase(approveInstance.rejected, handleRejected)
      .addCase(rejectInstance.pending, handlePending)
      .addCase(rejectInstance.rejected, handleRejected);
  },
});

export const { clearState } = journalApprovalSlice.actions;
export default journalApprovalSlice.reducer;
